/**
 * Utils - Various utility functions
 */

import { logger } from './hlsjs/utils/logger';
import Event from './HlsEvents';
import DetectedBrowser from './utils/detectBrowser';
import semver from 'semver';

/**
* @access internal
*/
class Utils {
	static get version() {
		return '__VERSION__';
	}

	static isFunction(func) {
		return typeof func === 'function';
	}

	static isObject(obj) {
		return obj && typeof obj === 'object';
	}

	static isArray(array) {
		return Array.isArray(array);
	}

	static isEmptyArray(array) {
		return Utils.isArray(array) && array.length === 0;
	}

	static isString(str) {
		return typeof str === 'string';
	}

	static isNull(value) {
		return value === null;
	}

	static isDefined(value) {
		return value !== undefined;
	}

	static isPhantom() {
		return ( Utils.isBrowser() && DetectedBrowser.name === 'phantom' );
	}

	static isChrome() {
		return ( Utils.isBrowser() && DetectedBrowser.name === 'chrome' );
	}

	static isOpera() {
		return ( Utils.isBrowser() && DetectedBrowser.name === 'opera' );
	}

	static isChromium() {
		return ( Utils.isBrowser() && (DetectedBrowser.name === 'chrome' || DetectedBrowser.name === 'opera') );
	}

	static isEdge() {
		return ( Utils.isBrowser() && DetectedBrowser.name === 'edge' );
	}

	static isIE() {
		return ( Utils.isBrowser() && DetectedBrowser.name === 'ie' );
	}

	static isElectron() {
		return ( Utils.isBrowser() && DetectedBrowser.name === 'electron' );
	}

	static isSafari() {
		return ( Utils.isBrowser() && DetectedBrowser.name === 'safari' );
	}

	static isFirefox() {
		return ( Utils.isBrowser() && DetectedBrowser.name === 'firefox' );
	}

	static isTizen() {
		return ( Utils.isBrowser() && DetectedBrowser.name === 'tizen' );
	}

	static isTizen2() {
		return ( Utils.isTizen() && semver.satisfies(DetectedBrowser.version, '2.x.x') );
	}

	static isTizen3() {
		return ( Utils.isTizen() && semver.satisfies(DetectedBrowser.version, '>=3.x.x') );
	}

	static isWebOS() {
		return ( Utils.isBrowser() && DetectedBrowser.name === 'webos' );
	}

	static isMacOS() {
		return ( DetectedBrowser.platform === 'macintosh' );
	}

	static isWin8Point1() {
		return ( DetectedBrowser.platform === 'windows' &&
		DetectedBrowser.platformVersion !== 'unknown' &&
		semver.satisfies(DetectedBrowser.platformVersion, '6.3.0') );
	}

	static isFirefoxMacOS() {
		return ( Utils.isFirefox() && Utils.isMacOS() );
	}

	static isFirefoxBadWorker() {
		//versions of Firefox for MacOS earlier than version 54 could cause browser crashes when workers were enabled
		//this has also been fixed in ESR 52.2 but we can't differentiate between 52.2 and 52.0
		return ( Utils.isFirefoxMacOS() && semver.satisfies(DetectedBrowser.version, '<54.0.0') );
	}

	static isIEWin8Point1() {
		return ( Utils.isIE() && Utils.isWin8Point1() );
	}

	static isBrowser() {
		return typeof navigator !== 'undefined';
	}

	static isChromiumMacOS() {
		return ( Utils.isChromium() && Utils.isMacOS() );
	}

	static areFunctionsDifferentiated() {
		let testFunction1 = function testFunction() {
			this._testFunctionProperty = 'TESTING';
		};
		let testFunction2 = function testFunction() {
			this._testFunctionProperty = 'TESTING';
		};

		// if the functions are differentiated, ie due to code coverage tools. this will pick it up.
		return testFunction1 !== testFunction2;
	}

	static isWallaby() {
		/*
			wallaby is a dynamic test runner. It runs tests in either PhantomJS or Electron (ie Chromium)
			if it is present the wallaby global will be.

			This matters because wallaby instruments functions, and if they're instrumented they can be
			differentiated, which affects tests.
		 */
		return typeof wallaby !== 'undefined';
	}

	static isWebpack() {
		/*
			webpack and browserify require different packages to run workers. This may affect functionality, and also
			requires some tests to be written to adapt to both possibilities.
		*/
		return typeof __webpack_modules__ !== 'undefined'; //eslint-disable-line camelcase
	}

	static hasWorker() {
		//TODO: should this return false if we're forcibly disabling workers (like in Firefox for Mac)?
		return typeof(Worker) !== 'undefined';
	}

	// property has to exist in the object's prototype, and be settable.
	static isPropertySettable(classInstance, prop)	{
		let settable = false;

		// if the property exists in the class instance
		if (prop in classInstance) {
			// get the property descriptor for this property from our prototype (which is the class definition)
			let propDescriptor = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(classInstance), prop);

			// if there is a set function for the property, then it a setter
			if (propDescriptor.set !== undefined) {
				settable = true;
			}
		}

		return settable;
	}

	static getClassName(classInstance) {
		// in ES6, the name of the constructor function is the same as the class.
		return classInstance.constructor.name;
	}

	static getClassPropertyName(classInstance, propetyName) {
		return Utils.getClassName(classInstance) + '.' + propetyName;
	}

	static applyValueToSetter(classInstance, prop, value) {
		if (Utils.isPropertySettable(classInstance, prop)) {
			logger.log('applyValueToSetter: ' + Utils.getClassPropertyName(classInstance, prop) + ' = ' + value);
			classInstance[prop] = value;
		}
		else {
			logger.log(`applyValueToSetter: ${Utils.getClassPropertyName(classInstance, prop)} is not settable (${value})`);
		}
	}

	static applyConfig(classInstance, config = {}) {
		// for all properties in the config
		for (let prop in config) {

			if (config.hasOwnProperty(prop)) {
				// applies the config property if the same property exists in class and is settable.
				Utils.applyValueToSetter(classInstance, prop, config[prop]);
			}
		}
	}

	static throwIfNotEvent(event) {
		if( Event[event] === undefined ) {
			throw new Error(`event: ${event} is not a known Event`);
		}
	}

	static propertyCanNotBeThrow(value, canNotBe, propertyName) {
		throw new Error(propertyName + ' can not be ' + canNotBe + ', ( ' + value + ' )');
	}

	static propertyCanNotBeWarn(value, canNotBe, propertyName) {
		logger.warn(propertyName + ' can not be ' + canNotBe + ', ( ' + value + ' ), the value will be adjusted accordingly');
	}

	static propertyMustBeThrow(value, mustBe, propertyName) {
		throw new Error(propertyName + ' must be ' + mustBe + ', ( ' + value + ' )');
	}

	static checkLessThanMinusOneAndThrow(value, propertyName) {
		if (value < -1) {
			Utils.propertyCanNotBeThrow(value, 'less than -1', propertyName);
		}
	}

	static checkLessThanZeroPointZeroAndThrow(value, propertyName) {
		if (value < 0.0) {
			Utils.propertyCanNotBeThrow(value, 'less than 0.0', propertyName);
		}
	}

	static checkLessThanZeroPointZeroAndWarn(value, propertyName) {
		if (value < 0.0) {
			Utils.propertyCanNotBeWarn(value, 'less than 0.0', propertyName);
		}
	}

	static checkEqualToZeroPointZeroAndThrow(value, propertyName) {
		if (value === 0.0) {
			Utils.propertyCanNotBeThrow(value, 'equal to 0.0', propertyName);
		}
	}

	static checkMoreThanOnePointZeroAndThrow(value, propertyName) {
		if (value > 1.0) {
			Utils.propertyCanNotBeThrow(value, 'more than 1.0', propertyName);
		}
	}

	static checkLessThanOnePointZeroAndThrow(value, propertyName) {
		if (value < 1.0) {
			Utils.propertyCanNotBeThrow(value, 'less than 1.0', propertyName);
		}
	}

	static checkNotIntegerAndThrow(value, propertyName) {
		if(!Number.isInteger) {
			if(typeof value !== 'number') {
				Utils.propertyMustBeThrow(value, 'an integer', propertyName);
			}
			if(!isFinite(value)) {
				Utils.propertyMustBeThrow(value, 'an integer', propertyName);
			}
			if((value % 1) !== 0) {
				Utils.propertyMustBeThrow(value, 'an integer', propertyName);
			}
		}
		else {
			if(!Number.isInteger(value)) {
				Utils.propertyMustBeThrow(value, 'an integer', propertyName);
			}
		}
	}

	static checkNotFiniteAndThrow(value, propertyName) {
		if(!Number.isFinite) {
			if(typeof value !== 'number') {
				Utils.propertyMustBeThrow(value, 'a finite number', propertyName);
			}
			if(!isFinite(value)) {
				Utils.propertyMustBeThrow(value, 'a finite number', propertyName);
			}
		}
		else {
			if(!Number.isFinite(value)) {
				Utils.propertyMustBeThrow(value, 'a finite number', propertyName);
			}
		}
	}

	static checkNotNumberAndThrow(value, propertyName) {
		if(typeof value !== 'number') {
			Utils.propertyMustBeThrow(value, 'a number', propertyName);
		}
	}

	static checkNotFunctionAndThrow(value, propertyName) {
		if(!Utils.isFunction(value)) {
			Utils.propertyMustBeThrow(value, 'a function', propertyName);
		}
	}

	static checkNotNullableFunctionAndThrow(value, propertyName) {
		if(!Utils.isFunction(value) && Utils.isDefined(value) && !Utils.isNull(value)) {
			Utils.propertyMustBeThrow(value, 'a function, undefined, or null', propertyName);
		}
	}

	static isValidDate(date) {
		let validDate = false;

		if ( Object.prototype.toString.call(date) === '[object Date]' ) {
			if ( !isNaN( date.getTime() ) ) {
				validDate = true;
			}
		}

		return validDate;
	}

	static msErrToHex(errCode) {
		//convert given microsoft error number into its hex format
		return '0x' + (errCode >>> 0).toString(16).toUpperCase();
	}

	static getTimeForPdt(pdt) {
		if(Utils.isValidDate(pdt)) {
			return pdt.getTime() / 1000;
		}
		else {
			return 0;
		}
	}

	static getPdtForTime(time) {
		return new Date(time * 1000);
	}

	static isPromiseOverloaded() {
		let result = false;

		//https://github.com/DefinitelyTyped/DefinitelyTyped/issues/10801
		// native fetch API returns a native Promise, if implemented (on Chrome/Safari)
		//let fetchPromise = fetch("");
		//if (Object.getPrototypeOf(fetch("")) !== Promise.prototype) {
/*
				let promiseProto = Promise.prototype;
				let promiseProto2 = Object.getPrototypeOf(Promise.resolve());
				let fetchPromiseProto = Object.getPrototypeOf(fetchPromise);

				let fetchInstanceOfPromise = !!(fetchPromise instanceof Promise);
				let fetchPromiseType = typeof fetchPromise;
				let fetchPromiseTypeName = type(fetchPromise);

				let test = Object.getOwnPropertyNames( fetchPromiseProto );
*/

		try {
			// We execute a fetch request, which returns a native Promise. Then we can
			// detect if Promise has been overidden, because it would differ to the native version.
			//
			// 'data:,' is the bare minimum data url we can request. '' is an alternative, but
			// that would result in a repeated request for the current origin, which in some pages
			// might result in undesired behaviour, eg, 403s
			//
			// browsers that this test is valid: Chrome, Safari 11
			// browsers that this test is invalid: Firefox, Edge, older Safari versions
			// browsers that throw: IE
			result = Utils.isChrome() && !(fetch('data:,') instanceof Promise);
		}
		catch (e) {
		}


		return result;
	}
}

export default Utils;



// WEBPACK FOOTER //
// ./src/HlsUtils.js